import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import {
	FormControl,
	FormHelperText,
	IconButton,
	InputAdornment,
	InputLabel,
	OutlinedInput,
} from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import type React from "react";

const theme = createTheme({
	components: {
		MuiOutlinedInput: {
			styleOverrides: {
				root: {
					borderRadius: "12px",
					".MuiOutlinedInput-notchedOutline": {
						borderColor: "#D4D4D8",
					},
					"&:hover .MuiOutlinedInput-notchedOutline": {
						borderColor: "#D4D4D8",
					},
					"&.Mui-focused .MuiOutlinedInput-notchedOutline": {
						borderColor: "#D4D4D8",
					},
					"&.Mui-error .MuiOutlinedInput-notchedOutline": {
						borderColor: "#F87171",
					},
				},
				input: {
					color: "#D4D4D8",
					"&:-webkit-autofill": {
						WebkitTextFillColor: "#D4D4D8 !important",
						WebkitBackgroundClip: "text !important",
						backgroundClip: "text !important",
						caretColor: "#D4D4D8 !important",
						transition: "background-color 5000000s ease-in-out 0s",
					},
					"&:-webkit-autofill:hover": {
						WebkitTextFillColor: "#D4D4D8 !important",
						WebkitBackgroundClip: "text !important",
						backgroundClip: "text !important",
						caretColor: "#D4D4D8 !important",
						transition: "background-color 5000000s ease-in-out 0s",
					},
					"&:-webkit-autofill:focus": {
						WebkitTextFillColor: "#D4D4D8 !important",
						WebkitBackgroundClip: "text !important",
						backgroundClip: "text !important",
						caretColor: "#D4D4D8 !important",
						transition: "background-color 5000000s ease-in-out 0s",
					},
					"&:-webkit-autofill:active": {
						WebkitTextFillColor: "#D4D4D8 !important",
						WebkitBackgroundClip: "text !important",
						backgroundClip: "text !important",
						caretColor: "#D4D4D8 !important",
						transition: "background-color 5000000s ease-in-out 0s",
					},
				},
				notchedOutline: {
					"& legend > span": {
						display: "none",
					},
				},
			},
		},
		MuiInputLabel: {
			styleOverrides: {
				root: {
					fontFamily: "Montserrat",
					fontSize: "16px",
					color: "#D4D4D8",
					transform: "translate(45px, 18px) scale(1)", // Default position
					transition: "transform 200ms ease-in-out",
					"&.Mui-focused": {
						color: "#D4D4D8",
						transform: "translate(12px, -20px) scale(0.75)", // Focused position
					},
					"&.MuiFormLabel-filled": {
						transform: "translate(12px, -20px) scale(0.75)", // Filled position
					},
					"&.Mui-error": {
						color: "#FCA5A5",
					},
				},
			},
		},
		MuiInputAdornment: {
			styleOverrides: {
				root: {
					fontSize: "24px",
					color: "#D4D4D8",
				},
			},
		},
		MuiFormHelperText: {
			styleOverrides: {
				root: {
					fontFamily: "Montserrat",
					"&.Mui-error": {
						color: "#FCA5A5",
					},
				},
			},
		},
	},
});

type CustomInputProps = {
	type: "email" | "number" | "password" | "search" | "text";
	className?: string;
	label: string;
	value: string;
	name: string;
	errorMessage?: string;
	startIcon?: React.ReactNode;
	endIcon?: React.ReactNode;
	onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
	onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
	onToggleVisibility?: () => void;
	showPassword?: boolean;
	autoComplete?: string;
};

const CustomInput: React.FC<CustomInputProps> = ({
	type,
	className,
	label,
	value,
	name,
	errorMessage,
	startIcon,
	endIcon,
	onChange,
	onToggleVisibility,
	showPassword,
	autoComplete,
}) => {
	return (
		<ThemeProvider theme={theme}>
			<FormControl
				fullWidth
				sx={{
					marginTop: 4,
					marginBottom: errorMessage ? "20px" : "4px",
					height: "56px",
				}}
				variant="outlined"
				error={!!errorMessage}
			>
				<InputLabel htmlFor={`${name}-input`} shrink>
					{label}
				</InputLabel>
				<OutlinedInput
					className={className}
					id={`${name}-input`}
					type={showPassword && type === "password" ? "text" : type}
					value={value}
					name={name}
					onChange={onChange}
					startAdornment={
						startIcon && (
							<InputAdornment position="start">{startIcon}</InputAdornment>
						)
					}
					endAdornment={
						endIcon ? (
							<InputAdornment position="end">{endIcon}</InputAdornment>
						) : (
							type === "password" && (
								<InputAdornment position="end">
									<IconButton
										onClick={onToggleVisibility}
										onMouseDown={(e) => e.preventDefault()}
										edge="end"
										sx={{ color: "#D4D4D8" }}
									>
										{showPassword ? (
											<VisibilityOffOutlinedIcon />
										) : (
											<VisibilityOutlinedIcon />
										)}
									</IconButton>
								</InputAdornment>
							)
						)
					}
					label={label}
					autoComplete={autoComplete}
				/>
				{errorMessage && <FormHelperText>{errorMessage}</FormHelperText>}
			</FormControl>
		</ThemeProvider>
	);
};

export default CustomInput;
